import { useCallback } from 'react';
import {
    collection,
    query,
    where,
    orderBy,
    getDocs,
    getCountFromServer,
    addDoc,
    deleteField,
    updateDoc,
    deleteDoc,
    doc,
    serverTimestamp,
    Timestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type {
    CurriculumLevel,
    CurriculumUnit,
    CurriculumSection,
    CurriculumLesson,
    CurriculumTechnique,
    TechniqueLevelMap,
} from '@/types';

/**
 * Data layer for the curriculum content manager (Phase A). All curriculum
 * business logic lives here per the hooks convention; the /admin/curriculum
 * components stay presentational.
 *
 * Hierarchy is Unit → Section → Lesson → Technique, each scoped to its parent
 * by a foreign-key field (unit.levelId, section.unitId, lesson.sectionId,
 * technique.lessonId) and ordered by `order`. Children are loaded lazily as the
 * admin expands a node — cheap and scales without composite indexes.
 *
 * Writes are HARD deletes (deleteDoc) — matching the common Firestore pattern in
 * this repo (roles, payouts, etc.). Firestore rules gate every write to isAdmin().
 */

const COLLECTIONS = {
    levels: 'curriculumLevels',
    units: 'curriculumUnits',
    sections: 'curriculumSections',
    lessons: 'curriculumLessons',
    techniques: 'curriculumTechniques',
    map: 'techniqueLevelMap',
} as const;

const toDate = (v: unknown): Date => {
    if (v instanceof Timestamp) return v.toDate();
    if (v instanceof Date) return v;
    if (typeof v === 'string' || typeof v === 'number') return new Date(v);
    return new Date();
};

/**
 * Drop keys whose value is `undefined`.
 *
 * This is not cosmetic. Firestore is initialized WITHOUT
 * `ignoreUndefinedProperties`, so `addDoc({ colorTag: undefined })` throws
 * "Unsupported field value: undefined" before anything reaches the server.
 * Every dialog in CurriculumDialogs builds its payload as
 *
 *     nameAr: nameAr.trim() || undefined
 *     colorTag: colorTag === 'none' ? undefined : colorTag
 *
 * so leaving ANY optional field at its default — which is the default state
 * of every Add dialog — made the whole save throw. The dialog catches it and
 * shows a generic "Failed to save.", which is exactly the dead "Add Unit"
 * button reported from production: the button opened, the form filled, and
 * the save died on an untouched optional field.
 *
 * Fixing it here rather than in each dialog means every curriculum write —
 * unit, section, lesson, technique, level, technique map — is covered, and a
 * new optional field cannot reintroduce the bug.
 */
const omitUndefined = (data: Record<string, unknown>): Record<string, unknown> =>
    Object.fromEntries(Object.entries(data).filter(([, v]) => v !== undefined));

// Stamp server-managed timestamp fields onto a write; the store owns them.
const stampCreate = (data: Record<string, unknown>) => ({
    ...omitUndefined(data),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
});
/**
 * On UPDATE, `undefined` means the author cleared an optional field, so it has
 * to become an explicit `deleteField()` — dropping the key like create does
 * would silently leave the old value behind (clear the Arabic name, save, and
 * it comes back). Only fields the dialog actually sent are touched.
 */
const stampUpdate = (data: Record<string, unknown>) => ({
    ...Object.fromEntries(
        Object.entries(data).map(([k, v]) => [k, v === undefined ? deleteField() : v]),
    ),
    updatedAt: serverTimestamp(),
});

export const useCurriculum = () => {
    // ---- LEVELS -----------------------------------------------------------
    const getLevels = useCallback(async (): Promise<CurriculumLevel[]> => {
        const snap = await getDocs(query(collection(db, COLLECTIONS.levels), orderBy('order', 'asc')));
        return snap.docs.map((d) => {
            const data = d.data();
            return {
                ...data,
                id: d.id,
                createdAt: toDate(data.createdAt),
                updatedAt: toDate(data.updatedAt),
            } as CurriculumLevel;
        });
    }, []);

    const createLevel = useCallback(async (data: Omit<CurriculumLevel, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
        const ref = await addDoc(collection(db, COLLECTIONS.levels), stampCreate(data as Record<string, unknown>));
        return ref.id;
    }, []);

    const updateLevel = useCallback(async (id: string, data: Partial<CurriculumLevel>): Promise<void> => {
        await updateDoc(doc(db, COLLECTIONS.levels, id), stampUpdate(data as Record<string, unknown>));
    }, []);

    const deleteLevel = useCallback(async (id: string): Promise<void> => {
        await deleteDoc(doc(db, COLLECTIONS.levels, id));
    }, []);

    // ---- generic child fetch by parent key --------------------------------
    const fetchByParent = useCallback(
        async <T,>(coll: string, parentField: string, parentId: string): Promise<T[]> => {
            const snap = await getDocs(
                query(collection(db, coll), where(parentField, '==', parentId), orderBy('order', 'asc')),
            );
            return snap.docs.map((d) => {
                const data = d.data();
                return {
                    ...data,
                    id: d.id,
                    createdAt: toDate(data.createdAt),
                    updatedAt: toDate(data.updatedAt),
                } as T;
            });
        },
        [],
    );

    // ---- UNITS (by levelId) ----------------------------------------------
    const getUnits = useCallback(
        (levelId: string) => fetchByParent<CurriculumUnit>(COLLECTIONS.units, 'levelId', levelId),
        [fetchByParent],
    );
    const createUnit = useCallback(async (data: Omit<CurriculumUnit, 'id' | 'createdAt' | 'updatedAt'>) => {
        const ref = await addDoc(collection(db, COLLECTIONS.units), stampCreate(data as Record<string, unknown>));
        return ref.id;
    }, []);
    const updateUnit = useCallback(async (id: string, data: Partial<CurriculumUnit>) => {
        await updateDoc(doc(db, COLLECTIONS.units, id), stampUpdate(data as Record<string, unknown>));
    }, []);
    const deleteUnit = useCallback(async (id: string) => {
        await deleteDoc(doc(db, COLLECTIONS.units, id));
    }, []);

    // ---- SECTIONS (by unitId) --------------------------------------------
    const getSections = useCallback(
        (unitId: string) => fetchByParent<CurriculumSection>(COLLECTIONS.sections, 'unitId', unitId),
        [fetchByParent],
    );
    const createSection = useCallback(async (data: Omit<CurriculumSection, 'id' | 'createdAt' | 'updatedAt'>) => {
        const ref = await addDoc(collection(db, COLLECTIONS.sections), stampCreate(data as Record<string, unknown>));
        return ref.id;
    }, []);
    const updateSection = useCallback(async (id: string, data: Partial<CurriculumSection>) => {
        await updateDoc(doc(db, COLLECTIONS.sections, id), stampUpdate(data as Record<string, unknown>));
    }, []);
    const deleteSection = useCallback(async (id: string) => {
        await deleteDoc(doc(db, COLLECTIONS.sections, id));
    }, []);

    // ---- LESSONS (by sectionId) ------------------------------------------
    const getLessons = useCallback(
        (sectionId: string) => fetchByParent<CurriculumLesson>(COLLECTIONS.lessons, 'sectionId', sectionId),
        [fetchByParent],
    );
    const createLesson = useCallback(async (data: Omit<CurriculumLesson, 'id' | 'createdAt' | 'updatedAt'>) => {
        const ref = await addDoc(collection(db, COLLECTIONS.lessons), stampCreate(data as Record<string, unknown>));
        return ref.id;
    }, []);
    const updateLesson = useCallback(async (id: string, data: Partial<CurriculumLesson>) => {
        await updateDoc(doc(db, COLLECTIONS.lessons, id), stampUpdate(data as Record<string, unknown>));
    }, []);
    const deleteLesson = useCallback(async (id: string) => {
        await deleteDoc(doc(db, COLLECTIONS.lessons, id));
    }, []);

    // ---- TECHNIQUES (by lessonId) ----------------------------------------
    const getTechniques = useCallback(
        (lessonId: string) => fetchByParent<CurriculumTechnique>(COLLECTIONS.techniques, 'lessonId', lessonId),
        [fetchByParent],
    );
    const createTechnique = useCallback(async (data: Omit<CurriculumTechnique, 'id' | 'createdAt' | 'updatedAt'>) => {
        const ref = await addDoc(collection(db, COLLECTIONS.techniques), stampCreate(data as Record<string, unknown>));
        return ref.id;
    }, []);
    const updateTechnique = useCallback(async (id: string, data: Partial<CurriculumTechnique>) => {
        await updateDoc(doc(db, COLLECTIONS.techniques, id), stampUpdate(data as Record<string, unknown>));
    }, []);
    const deleteTechnique = useCallback(async (id: string) => {
        await deleteDoc(doc(db, COLLECTIONS.techniques, id));
    }, []);

    // ---- counts (landing page) -------------------------------------------
    const countUnitsForLevel = useCallback(async (levelId: string): Promise<number> => {
        const snap = await getCountFromServer(
            query(collection(db, COLLECTIONS.units), where('levelId', '==', levelId)),
        );
        return snap.data().count;
    }, []);

    // ---- junction (cross-level technique reuse) --------------------------
    const getTechniqueLevelMaps = useCallback(
        (levelId: string) => fetchByParent<TechniqueLevelMap>(COLLECTIONS.map, 'levelId', levelId),
        [fetchByParent],
    );
    const mapTechniqueToLevel = useCallback(
        async (data: Omit<TechniqueLevelMap, 'id' | 'createdAt' | 'updatedAt'>) => {
            const ref = await addDoc(collection(db, COLLECTIONS.map), stampCreate(data as Record<string, unknown>));
            return ref.id;
        },
        [],
    );
    const unmapTechniqueFromLevel = useCallback(async (id: string) => {
        await deleteDoc(doc(db, COLLECTIONS.map, id));
    }, []);

    return {
        getLevels, createLevel, updateLevel, deleteLevel,
        getUnits, createUnit, updateUnit, deleteUnit,
        getSections, createSection, updateSection, deleteSection,
        getLessons, createLesson, updateLesson, deleteLesson,
        getTechniques, createTechnique, updateTechnique, deleteTechnique,
        countUnitsForLevel,
        getTechniqueLevelMaps, mapTechniqueToLevel, unmapTechniqueFromLevel,
    };
};
